/**
 * The impacts, risks and opportunities found to be material.
 *
 * Everything this kind is: how it is made, how it is read back, what it looks
 * like on the page, and how it is edited. Nothing outside this folder needs to
 * know the kind exists.
 */

import { readArray, readString } from '@/domain/json'
import type { JsonValue } from '@/domain/json'
import { newId, newIroEntry, type IroEntry, type IroSection } from '@/domain/section'
import IroBlock from '@/sections/iro/IroBlock.vue'
import IroInspector from '@/sections/iro/IroInspector.vue'
import type { SectionDefinition } from '@/sections/types'

/**
 * A stored value, if it is one of the offered options.
 *
 * Each of these is a closed list, unlike a disclosure reference: the standards
 * distinguish exactly three kinds of thing, three places in the value chain
 * and three horizons, and a fourth would be a typo rather than a case this
 * does not know about.
 */
function oneOf<T extends string>(options: readonly T[], value: unknown, fallback: T): T {
  return typeof value === 'string' && (options as readonly string[]).includes(value)
    ? (value as T)
    : fallback
}

export const IRO_TYPES = ['impact', 'risk', 'opportunity'] as const
export const IRO_POSITIONS = ['upstream', 'own', 'downstream'] as const
export const IRO_HORIZONS = ['short', 'medium', 'long'] as const

function readEntry(value: JsonValue): IroEntry {
  return {
    id: readString(value, 'id') || newId(),
    topic: readString(value, 'topic'),
    type: oneOf(IRO_TYPES, readString(value, 'type'), 'impact'),
    position: oneOf(IRO_POSITIONS, readString(value, 'position'), 'own'),
    horizon: oneOf(IRO_HORIZONS, readString(value, 'horizon'), 'short'),
    conclusion: readString(value, 'conclusion'),
  }
}

export const iroSection: SectionDefinition<'iro', IroSection> = {
  kind: 'iro',
  band: 'sustainability',

  icon: ['M2.5 4.5h11', 'M2.5 8h11', 'M2.5 11.5h11', 'M6 3v10'],

  create: (id, title) => ({
    id,
    kind: 'iro',
    title,
    entries: [newIroEntry(), newIroEntry()],
    caption: '',
  }),

  read: ({ id, title, stored }) => ({
    id,
    kind: 'iro',
    title,
    entries: readArray(stored, 'entries').map(readEntry),
    caption: readString(stored, 'caption'),
  }),

  block: IroBlock,
  inspector: IroInspector,
}
